/**
 * Modal accessibility primitives (G8).
 *
 * Encapsulates the keyboard / focus concerns every `role="dialog"` needs:
 *
 *   - Escape dismissal
 *   - Tab / Shift+Tab focus trap that wraps at the ends
 *   - Initial focus on the container if no child owns it
 *   - Focus restored to the previously-focused element on close
 *   - Body scroll lock while open
 *
 * Extracted from `components/Modal.jsx` so future dialog surfaces
 * (roundtable config, panel picker, and any §7h roundtable affordances)
 * can reuse the behaviour without copy-pasting the inline `useEffect`.
 * The Modal shell still owns layout, backdrop handling, and the close
 * button — this hook is only the keyboard/focus contract.
 */

import { useEffect, useRef } from "react";

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * @param {object} args
 * @param {boolean} args.open — whether the dialog is mounted
 * @param {React.RefObject<HTMLElement>} args.containerRef — the dialog card.
 *   Must be a stable ref (returned from `useRef` in the caller), not a ref
 *   that changes identity across renders — the focus-trap effect lists it
 *   as a dependency and would re-run (tearing down scroll lock, refocusing
 *   the container) if the ref object itself changed.
 * @param {() => void} args.onClose — dismiss handler (fires on Escape)
 */
export function useModalAccessibility({ open, containerRef, onClose }) {
  const previousFocusRef = useRef(null);
  // Callers commonly pass a fresh closure for onClose on every render
  // (e.g. inline `() => setOpen(false)`). If we put onClose directly in
  // the effect dependency array, the effect re-runs on every keystroke,
  // which blurs the active input via the cleanup's focus-restore and
  // then refocuses the container — on mobile browsers that cascades
  // back to the first input in the form. Route onClose through a ref
  // so the handler stays current without triggering effect re-runs.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  // Snapshot the element that had focus before the dialog opened so we
  // can restore it on close. Deliberately a separate effect so the
  // snapshot is taken once at open and not refreshed while the trap
  // moves focus around inside the dialog.
  useEffect(() => {
    if (open) {
      previousFocusRef.current = document.activeElement;
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const container = containerRef.current;
    if (!container) return;

    // If no child claimed focus via autoFocus, focus the container
    // itself (it needs tabIndex=-1 to receive programmatic focus) so
    // screen readers announce the dialog and keyboard events land
    // inside it.
    if (!container.contains(document.activeElement)) {
      container.focus();
    }

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleKey = (e) => {
      if (e.key === "Escape") {
        onCloseRef.current?.();
        return;
      }

      if (e.key !== "Tab") return;

      const focusable = [...container.querySelectorAll(FOCUSABLE)];
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleKey);

    return () => {
      document.removeEventListener("keydown", handleKey);
      document.body.style.overflow = prevOverflow;
      previousFocusRef.current?.focus();
    };
  }, [open, containerRef]);
}
